import { memo, useMemo, useRef, useState } from "react";
import { Chart } from "primereact/chart";
import { Panel } from "primereact/panel";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import { useReport } from "../../context/report";

const METRICAS = [
    { label: "Trama", value: "trama" },
    { label: "Urdume", value: "urdume" },
    { label: "Trama + Urdume", value: "total" },
];

const ROTULO_METRICA = {
    trama: "trama",
    urdume: "urdume",
    total: "trama + urdume",
};

// "dd/mm/yy" -> Date
const parseData = (str) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{2,4})$/.exec(String(str).trim());
    if (!m) return null;
    const ano = Number(m[3]) < 100 ? 2000 + Number(m[3]) : Number(m[3]);
    return new Date(ano, Number(m[2]) - 1, Number(m[1]));
};

const fmt1 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export default memo(function QuebrasControle() {
    const { data } = useReport();

    const [metrica, setMetrica] = useState("trama");
    const [limSup, setLimSup] = useState(16);
    const [limInf, setLimInf] = useState(8);
    const [artigos, setArtigos] = useState([]);
    const [teares, setTeares] = useState([]);
    const [ordemDecrescente, setOrdemDecrescente] = useState(false);
    const [mostrarMedia, setMostrarMedia] = useState(true);
    const [tipoGrafico, setTipoGrafico] = useState("line");

    const chartRef = useRef(null);
    const printRef = useRef(null);
    const [printing, setPrinting] = useState(false);
    const [imagem, setImagem] = useState(null);

    // O gráfico é impresso como imagem para manter as cores e o tamanho
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: "@page { size: A4 landscape; margin: 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }",
        onBeforeGetContent: () =>
            new Promise((resolve) => {
                const ref = chartRef.current;
                const chart = ref?.getChart?.();
                const img = chart ? chart.toBase64Image() : ref?.getBase64Image?.();
                setImagem(img || null);
                setPrinting(true);
                setTimeout(resolve, 500);
            }),
        onAfterPrint: () => {
            setPrinting(false);
            setImagem(null);
        },
    });

    // Quantidade de dias distintos filtrados no período
    const diasFiltrados = useMemo(() => {
        if (!data?.datatable) return 0;
        const datas = new Set();
        data.datatable.forEach((r) => {
            if (r.turma === "Total") return;
            const dStr = String(r.data || "").trim();
            if (dStr && dStr !== "Período") {
                datas.add(dStr);
            }
        });
        return datas.size;
    }, [data]);

    const fatorDias = diasFiltrados > 0 ? diasFiltrados : 1;

    // Registros do backend (sem as linhas "Total")
    const registros = useMemo(() => {
        if (!data?.datatable) return [];
        const lista = [];
        data.datatable.forEach((r) => {
            if (r.turma === "Total") return;

            // Ignora registros sem produção (tear parado / importação incorreta)
            const eficiencia = Number(r.eficiencia);
            if (r.eficiencia !== null && r.eficiencia !== undefined && r.eficiencia !== "" && eficiencia <= 0) return;

            const date = parseData(r.data);
            if (!date) return;
            lista.push({
                tear: String(r.tear),
                artigo: r.artigo === null || r.artigo === undefined || r.artigo === "" ? "-" : String(r.artigo),
                turma: r.turma,
                date,
                trama: Number(r.trama) || 0,
                urdume: Number(r.urdume) || 0,
            });
        });
        return lista;
    }, [data]);

    const artigoOptions = useMemo(
        () =>
            [...new Set(registros.map((r) => r.artigo))]
                .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
                .map((a) => ({ label: a, value: a })),
        [registros]
    );

    const tearOptions = useMemo(
        () =>
            [...new Set(registros.map((r) => r.tear))]
                .sort((a, b) => Number(a) - Number(b))
                .map((t) => ({ label: `Tear ${t}`, value: t })),
        [registros]
    );

    // Total de quebras por tear considerando os dias e filtros selecionados
    const { dadosTeares, mediaGeral } = useMemo(() => {
        const mapa = new Map();

        registros.forEach((r) => {
            if (artigos.length > 0 && !artigos.includes(r.artigo)) return;
            if (teares.length > 0 && !teares.includes(r.tear)) return;

            const valor = metrica === "trama" ? r.trama : metrica === "urdume" ? r.urdume : r.trama + r.urdume;

            if (!mapa.has(r.tear)) {
                mapa.set(r.tear, {
                    tear: r.tear,
                    rotulo: `Tear ${r.tear}`,
                    total: 0,
                    trama: 0,
                    urdume: 0,
                });
            }

            const item = mapa.get(r.tear);
            item.trama += r.trama;
            item.urdume += r.urdume;
            item.total += valor;
        });

        // Se o usuário filtrou teares específicos mas algum teve 0 quebras no filtro de artigo, inclui com 0
        if (teares.length > 0) {
            teares.forEach((t) => {
                if (!mapa.has(t)) {
                    mapa.set(t, {
                        tear: t,
                        rotulo: `Tear ${t}`,
                        total: 0,
                        trama: 0,
                        urdume: 0,
                    });
                }
            });
        }

        let lista = [...mapa.values()];

        if (ordemDecrescente) {
            lista.sort((a, b) => b.total - a.total || Number(a.tear) - Number(b.tear));
        } else {
            lista.sort((a, b) => Number(a.tear) - Number(b.tear));
        }

        const somaTotal = lista.reduce((acc, cur) => acc + cur.total, 0);
        const media = lista.length > 0 ? somaTotal / lista.length : null;

        return { dadosTeares: lista, mediaGeral: media };
    }, [registros, artigos, teares, metrica, ordemDecrescente]);

    const temSup = limSup !== null && limSup !== undefined && limSup !== "";
    const temInf = limInf !== null && limInf !== undefined && limInf !== "";

    // Limites multiplicados pela quantidade de dias filtrados
    const limSupEfetivo = temSup ? Number(((limSup || 0) * fatorDias).toFixed(2)) : null;
    const limInfEfetivo = temInf ? Number(((limInf || 0) * fatorDias).toFixed(2)) : null;

    const acimaSup = temSup ? dadosTeares.filter((t) => t.total > limSupEfetivo).length : 0;
    const abaixoInf = temInf ? dadosTeares.filter((t) => t.total < limInfEfetivo).length : 0;
    const limitesInvertidos = temSup && temInf && limInf > limSup;

    const chartData = useMemo(() => {
        const labels = dadosTeares.map((d) => d.rotulo);
        const datasets = [
            {
                type: tipoGrafico,
                label: `Total Quebras (${ROTULO_METRICA[metrica]})`,
                data: dadosTeares.map((d) => d.total),
                borderColor: "#2f5597",
                backgroundColor: tipoGrafico === "bar" ? "rgba(47, 85, 151, 0.85)" : "#2f5597",
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2,
                tension: 0,
            },
        ];

        if (mostrarMedia && mediaGeral !== null) {
            const valMedia = Number(mediaGeral.toFixed(2));
            datasets.push({
                type: "line",
                label: `Média (${fmt1.format(valMedia)})`,
                data: dadosTeares.map(() => valMedia),
                borderColor: "#e67e22",
                backgroundColor: "#e67e22",
                borderDash: [4, 4],
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 0,
            });
        }

        if (temSup) {
            datasets.push({
                type: "line",
                label: `Limite superior (${fmt1.format(limSupEfetivo)})`,
                data: dadosTeares.map(() => limSupEfetivo),
                borderColor: "#c00000",
                backgroundColor: "#c00000",
                borderDash: [6, 4],
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 0,
            });
        }

        if (temInf) {
            datasets.push({
                type: "line",
                label: `Limite inferior (${fmt1.format(limInfEfetivo)})`,
                data: dadosTeares.map(() => limInfEfetivo),
                borderColor: "#00803c",
                backgroundColor: "#00803c",
                borderDash: [6, 4],
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 0,
            });
        }

        return { labels, datasets };
    }, [dadosTeares, tipoGrafico, metrica, mostrarMedia, mediaGeral, temSup, limSupEfetivo, temInf, limInfEfetivo]);

    const options = {
        maintainAspectRatio: false,
        animation: printing ? false : undefined,
        interaction: { mode: "index", intersect: false },
        plugins: {
            datalabels: { display: false },
            title: {
                display: true,
                text: `Gráfico de controle de quebras de ${ROTULO_METRICA[metrica]} por tear - ${data?.periodo || ""}`,
                font: { size: 18 },
            },
            legend: { position: "bottom" },
            tooltip: {
                callbacks: {
                    afterBody: (context) => {
                        const index = context[0]?.dataIndex;
                        const item = dadosTeares[index];
                        if (!item) return [];
                        const linhas = [];
                        if (metrica === "total") {
                            linhas.push(`  Trama: ${fmt1.format(item.trama)}`);
                            linhas.push(`  Urdume: ${fmt1.format(item.urdume)}`);
                        }
                        if (diasFiltrados > 0) {
                            linhas.push(`  Média diária: ${fmt1.format(item.total / diasFiltrados)} / dia`);
                        }
                        return linhas;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: `Total de Quebras (${ROTULO_METRICA[metrica]})` },
            },
            x: {
                title: { display: true, text: "Teares" },
                ticks: { maxRotation: 90, minRotation: 45, font: { size: 11 } },
            },
        },
    };

    const limpar = () => {
        setArtigos([]);
        setTeares([]);
        setMetrica("trama");
        setLimSup(16);
        setLimInf(8);
        setOrdemDecrescente(false);
        setMostrarMedia(true);
        setTipoGrafico("line");
    };

    return (
        <Panel header="Gráfico de controle" toggleable collapsed={true}>
            <div className="p-fluid p-formgrid p-grid p-mb-2">
                <div className="p-field p-col-12 p-md-3">
                    <label>Artigo</label>
                    <MultiSelect
                        value={artigos}
                        options={artigoOptions}
                        onChange={(e) => setArtigos(e.value)}
                        placeholder="Todos os artigos"
                        display="chip"
                        filter
                    />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>Teares</label>
                    <MultiSelect
                        value={teares}
                        options={tearOptions}
                        onChange={(e) => setTeares(e.value)}
                        placeholder="Todos os teares"
                        display="chip"
                        filter
                    />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>
                        Limite superior (por dia)
                        {diasFiltrados > 0 && temSup && (
                            <span style={{ fontSize: "11px", color: "#666", fontWeight: "normal", marginLeft: 4 }}>
                                (Total: {fmt1.format(limSupEfetivo)})
                            </span>
                        )}
                    </label>
                    <InputNumber
                        value={limSup}
                        onValueChange={(e) => setLimSup(e.value)}
                        placeholder="Sem limite"
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        showButtons
                    />
                    <small style={{ color: "#777", display: "block", marginTop: "2px" }}>
                        {diasFiltrados > 0
                            ? `${limSup || 0} × ${diasFiltrados} dia(s) = ${fmt1.format((limSup || 0) * fatorDias)}`
                            : "Multiplicado pelos dias filtrados"}
                    </small>
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>
                        Limite inferior (por dia)
                        {diasFiltrados > 0 && temInf && (
                            <span style={{ fontSize: "11px", color: "#666", fontWeight: "normal", marginLeft: 4 }}>
                                (Total: {fmt1.format(limInfEfetivo)})
                            </span>
                        )}
                    </label>
                    <InputNumber
                        value={limInf}
                        onValueChange={(e) => setLimInf(e.value)}
                        placeholder="Sem limite"
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        showButtons
                    />
                    <small style={{ color: "#777", display: "block", marginTop: "2px" }}>
                        {diasFiltrados > 0
                            ? `${limInf || 0} × ${diasFiltrados} dia(s) = ${fmt1.format((limInf || 0) * fatorDias)}`
                            : "Multiplicado pelos dias filtrados"}
                    </small>
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap p-mb-3">
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Métrica:</span>
                    <SelectButton
                        value={metrica}
                        options={METRICAS}
                        onChange={(e) => e.value && setMetrica(e.value)}
                        unselectable={false}
                    />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Ordenação:</span>
                    <SelectButton
                        value={ordemDecrescente ? "desc" : "padrao"}
                        options={[
                            { label: "Nº do Tear", value: "padrao" },
                            { label: "Mais Quebras (Decrescente)", value: "desc" },
                        ]}
                        onChange={(e) => e.value && setOrdemDecrescente(e.value === "desc")}
                        unselectable={false}
                    />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Tipo:</span>
                    <SelectButton
                        value={tipoGrafico}
                        options={[
                            { label: "Linha", value: "line" },
                            { label: "Barra", value: "bar" },
                        ]}
                        onChange={(e) => e.value && setTipoGrafico(e.value)}
                        unselectable={false}
                    />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <Checkbox
                        inputId="qc-media"
                        checked={mostrarMedia}
                        onChange={(e) => setMostrarMedia(e.checked)}
                    />
                    <label htmlFor="qc-media" className="p-ml-2" style={{ cursor: "pointer" }}>
                        Mostrar linha de média
                    </label>
                </div>
                <div className="p-d-flex p-ai-center p-mb-2">
                    <Button
                        className="p-button-outlined p-mr-2"
                        label="Limpar filtros"
                        icon="pi pi-filter-slash"
                        onClick={limpar}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                    <Button
                        label="Imprimir"
                        icon="pi pi-print"
                        onClick={handlePrint}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                </div>
            </div>

            {limitesInvertidos && (
                <p style={{ color: "#c00000", margin: "0 0 8px" }}>
                    O limite inferior está maior que o limite superior.
                </p>
            )}

            {dadosTeares.length === 0 ? (
                <p>Nenhum dado encontrado com os filtros selecionados.</p>
            ) : (
                <>
                    <Chart
                        ref={chartRef}
                        key={tipoGrafico}
                        id="chartControle"
                        type={tipoGrafico}
                        data={chartData}
                        options={options}
                        height="450px"
                        width="100%"
                    />
                    <div style={{ fontSize: 12, color: "#444", marginTop: 8 }}>
                        {diasFiltrados > 0 && (
                            <>Período com <strong>{diasFiltrados}</strong> dia(s) filtrado(s). </>
                        )}
                        Total de teares exibidos: <strong>{dadosTeares.length}</strong>.
                        {mediaGeral !== null && (
                            <>
                                {" "}Média por tear no período: <strong>{fmt1.format(mediaGeral)}</strong> quebras
                                {diasFiltrados > 0 && (
                                    <> (média diária: <strong>{fmt1.format(mediaGeral / diasFiltrados)}</strong> quebras/dia)</>
                                )}.
                            </>
                        )}
                        {temSup && (
                            <>
                                {" "}Teares acima do limite superior ({fmt1.format(limSupEfetivo)}):{" "}
                                <strong style={{ color: acimaSup > 0 ? "#c00000" : "inherit" }}>{acimaSup}</strong>.
                            </>
                        )}
                        {temInf && (
                            <>
                                {" "}Teares abaixo do limite inferior ({fmt1.format(limInfEfetivo)}):{" "}
                                <strong style={{ color: abaixoInf > 0 ? "#00803c" : "inherit" }}>{abaixoInf}</strong>.
                            </>
                        )}
                    </div>
                </>
            )}

            {/* Conteúdo usado apenas na impressão */}
            <div style={{ position: "absolute", left: "-99999px", top: 0 }}>
                <div ref={printRef} style={{ padding: "20px", textAlign: "center" }}>
                    {printing && imagem && (
                        <img src={imagem} alt="Gráfico de controle" style={{ maxWidth: "100%" }} />
                    )}
                </div>
            </div>
        </Panel>
    );
});